import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Bell, Radio, Megaphone, Award, LifeBuoy, MessageSquareHeart, CheckCheck, Trash2, X } from "lucide-react";

const TYPE_META = {
  new_episode: { icon: Radio, label: "New Episodes", tone: "text-brand bg-brand/10" },
  nudge: { icon: Megaphone, label: "Nudges", tone: "text-brand-terracotta bg-brand-terracotta/10" },
  reward: { icon: Award, label: "Rewards", tone: "text-brand-dough bg-brand-dough/20" },
  ticket: { icon: LifeBuoy, label: "Help Desk", tone: "text-brand bg-brand/10" },
  feedback: { icon: MessageSquareHeart, label: "Feedback", tone: "text-brand bg-brand/10" },
};

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function Inbox() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications/inbox");
      setItems(data.notifications);
    } catch {
      setItems([]);
      toast.error("Couldn't load your notifications. Please try again.");
    }
  };
  useEffect(() => { load(); }, []);

  const undoClear = async (ids) => {
    try { await api.post("/notifications/undo-clear", { ids }); toast.success("Restored"); load(); }
    catch { toast.error("Couldn't undo."); }
  };
  const markAllRead = async () => { await api.post("/notifications/read"); toast.success("All marked as read"); load(); };
  const clearRead = async () => {
    const { data } = await api.post("/notifications/clear-read");
    load();
    if (data.cleared > 0) {
      toast.success(`Cleared ${data.cleared} read item${data.cleared === 1 ? "" : "s"}`, {
        duration: 9000, action: { label: "Undo", onClick: () => undoClear(data.ids) },
      });
    } else toast.info("No read items to clear");
  };
  const dismiss = async (id) => {
    await api.post(`/notifications/${id}/dismiss`);
    load();
    toast.success("Dismissed", { duration: 5000, action: { label: "Undo", onClick: () => undoClear([id]) } });
  };
  const clearType = async (type, label) => {
    const { data } = await api.post(`/notifications/clear-type?type=${type}`);
    load();
    if (data.cleared > 0) {
      toast.success(`Cleared ${data.cleared} ${label.toLowerCase()} item${data.cleared === 1 ? "" : "s"}`, {
        duration: 9000, action: { label: "Undo", onClick: () => undoClear(data.ids) },
      });
    }
  };
  const open = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    if (n.link) navigate(n.link);
    else load();
  };

  // group by type
  const groups = {};
  (items || []).forEach((n) => { (groups[n.type] = groups[n.type] || []).push(n); });
  const order = ["new_episode", "nudge", "reward", "ticket", "feedback"];
  const typeKeys = [...order.filter((k) => groups[k]), ...Object.keys(groups).filter((k) => !order.includes(k))];
  const unreadTotal = (items || []).filter((n) => !n.read).length;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
      <PageHeader title="Notifications" subtitle={items ? `${unreadTotal} unread · ${items.length} total` : "Your inbox"}>
        <Button variant="outline" onClick={markAllRead} className="rounded-full border-brand-line" data-testid="mark-all-read"><CheckCheck className="w-4 h-4 mr-2" /> Mark all read</Button>
        <Button variant="outline" onClick={clearRead} className="rounded-full border-brand-line" data-testid="clear-read"><Trash2 className="w-4 h-4 mr-2" /> Clear read</Button>
      </PageHeader>

      {!items ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-line p-12 text-center" data-testid="inbox-empty">
          <Bell className="w-8 h-8 text-brand-muted mx-auto" />
          <p className="font-display text-2xl text-brand-ink mt-3">You're all caught up</p>
          <p className="text-brand-muted mt-1">New episode alerts, nudges, and rewards will show up here.</p>
        </div>
      ) : (
        <div className="space-y-8" data-testid="inbox-groups">
          {typeKeys.map((type) => {
            const meta = TYPE_META[type] || { icon: Bell, label: type, tone: "text-brand-muted bg-brand-sidebar" };
            const Icon = meta.icon;
            return (
              <section key={type} data-testid={`inbox-group-${type}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className={`h-7 w-7 rounded-lg flex items-center justify-center ${meta.tone}`}><Icon className="w-4 h-4" /></span>
                  <h3 className="font-display text-xl text-brand-ink">{meta.label}</h3>
                  <span className="text-sm text-brand-muted">({groups[type].length})</span>
                  <button onClick={() => clearType(type, meta.label)} className="ml-auto text-xs text-brand-muted hover:text-brand-terracotta font-semibold transition-colors" data-testid={`clear-group-${type}`}>
                    Clear all
                  </button>
                </div>
                <div className="space-y-2">
                  {groups[type].map((n) => (
                    <div key={n.id} data-testid={`inbox-item-${n.id}`}
                      className={`group flex items-start gap-3 rounded-xl border p-4 transition-colors duration-200 cursor-pointer ${n.read ? "border-brand-line bg-white" : "border-brand/30 bg-brand/[0.04]"}`}
                      onClick={() => open(n)}>
                      {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-brand shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm ${n.read ? "text-brand-ink" : "font-semibold text-brand-ink"}`}>{n.title}</p>
                        <p className="text-sm text-brand-muted">{n.message}</p>
                        <p className="text-xs text-brand-muted mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); dismiss(n.id); }} className="opacity-50 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity text-brand-muted hover:text-brand-terracotta shrink-0" data-testid={`inbox-dismiss-${n.id}`} aria-label="Dismiss notification">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
